import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getDownloadsData } from "../../../apis/dashboardApi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const Downloads = () => {
  const [filter, setFilter] = useState("day");
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      const params = { type: filter, startDate, endDate };
      try {
        const response = await getDownloadsData(params);
        const downloadsData = response?.result || [];

        const tableDownloads = downloadsData.map((item) => ({
          TimePeriod: item.TimePeriod,
          Type: item.Type,
          TotalDownloads: item.TotalDownloads,
        }));

        const filteredTableData = tableDownloads.filter((row) => {
          const rowDate = dayjs(row.TimePeriod);
          return (
            rowDate.isSameOrAfter(dayjs(startDate)) &&
            rowDate.isSameOrBefore(dayjs(endDate))
          );
        });

        setTableData(filteredTableData);
        setChartData(processChartData(downloadsData, filter));
      } catch (error) {
        console.error("Error fetching downloads data:", error);
      }
    };

    fetchData();
  }, [filter, startDate, endDate]);

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setDefaultDateRange(e.target.value);
  };

  const setDefaultDateRange = (filterType) => {
    const today = new Date();
    let start, end;
    switch (filterType) {
      case "week":
        start = dayjs(today).subtract(7, "day").toDate();
        break;
      case "month":
        start = dayjs(today).subtract(28, "day").toDate();
        break;
      case "year":
        start = dayjs(today).startOf("year").toDate();
        break;
      case "day":
        start = end = today;
        break;
      default:
        start = end = today;
    }
    setStartDate(start);
    setEndDate(end || today);
  };

  const processChartData = (data, period) => {
    const result = [];
    switch (period) {
      case "day":
        result.push(
          ...Array.from({ length: 24 }, (_, i) => ({
            hour: `${i}:00`,
            Android: 0,
            Apple: 0,
          })),
        );
        data.forEach((d) => {
          const hour = dayjs(d.TimePeriod).hour();
          if (d.Type === "Android") {
            result[hour].Android += parseInt(d.TotalDownloads, 10);
          } else if (d.Type === "Apple") {
            result[hour].Apple += parseInt(d.TotalDownloads, 10);
          }
        });
        break;
      case "week":
      case "month":
        result.push(
          ...Array.from(
            { length: dayjs(endDate).diff(dayjs(startDate), "day") + 1 },
            (_, i) => ({
              day: dayjs(startDate).add(i, "day").format("MM-DD"),
              Android: 0,
              Apple: 0,
            }),
          ),
        );
        data.forEach((d) => {
          const dayIndex = dayjs(d.TimePeriod).diff(dayjs(startDate), "day");
          if (result[dayIndex]) {
            if (d.Type === "Android") {
              result[dayIndex].Android += parseInt(d.TotalDownloads, 10);
            } else if (d.Type === "Apple") {
              result[dayIndex].Apple += parseInt(d.TotalDownloads, 10);
            }
          }
        });
        break;
      case "year":
        const monthsOfYear = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        result.push(
          ...monthsOfYear.map((month) => ({ month, Android: 0, Apple: 0 })),
        );
        data.forEach((d) => {
          const monthIndex = dayjs(d.TimePeriod).month();
          if (d.Type === "Android") {
            result[monthIndex].Android += parseInt(d.TotalDownloads, 10);
          } else if (d.Type === "Apple") {
            result[monthIndex].Apple += parseInt(d.TotalDownloads, 10);
          }
        });
        break;
      default:
        break;
    }
    return result;
  };

  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedData = tableData?.slice(startIdx, endIdx);
  const totalPages = Math.ceil(tableData?.length / pageSize);

  return (
    <div className="graph-section">
      <h4>Downloads by {filter}</h4>

      <div className="filter-section" style={{ marginBottom: "20px" }}>
        <div className="select-period-wrapper">
          <label>Select Period: </label>
          <select
            onChange={handleFilterChange}
            value={filter}
            className="datepic"
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
        </div>

        <div className="date-range-wrapper">
          <label>Start Date: </label>
          <DatePicker
            selected={startDate}
            onChange={(date) => {
              setStartDate(date);
              if (dayjs(date).isAfter(dayjs(endDate))) {
                setEndDate(date);
              }
            }}
            maxDate={new Date()}
            dateFormat="MM-dd-yyyy"
            className="datepic"
          />
        </div>
        <div className="date-range-wrapper">
          <label>End Date: </label>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            minDate={startDate}
            maxDate={new Date()}
            dateFormat="MM-dd-yyyy"
            className="datepic"
          />
        </div>
      </div>

      {chartData.length > 0 ? (
        <ResponsiveContainer
          width="100%"
          height={300}
          className="line-chart-wrapper"
        >
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: -25, bottom: 5 }}
          >
            {filter === "day" && <XAxis dataKey="hour" />}
            {filter === "week" && <XAxis dataKey="day" />}
            {filter === "month" && <XAxis dataKey="day" />}
            {filter === "year" && <XAxis dataKey="month" />}
            <YAxis />
            <Tooltip cursor={true} />
            <Line
              type="monotone"
              dataKey="Android"
              stroke="var(--one-orange)"
            />
            <Line type="monotone" dataKey="Apple" stroke="var(--one-red)" />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          There are no downloads for the selected period.
        </div>
      )}

      <div style={{ marginTop: "20px" }}>
        <h4>Download Details</h4>
        <table className="styled-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Time Period</th>
              <th>Type</th>
              <th>Total Downloads</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr key={index}>
                <td>{row.TimePeriod}</td>
                <td>{row.Type}</td>
                <td>{row.TotalDownloads}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination" style={{ marginTop: "20px" }}>
          <div>
            Page:
            <input
              type="number"
              value={currentPage}
              onChange={(e) => setCurrentPage(Number(e.target.value))}
              min={1}
              max={totalPages}
            />
            of {totalPages}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Downloads;
